import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

export type SubCustomer = {
  // SubCurrAccCode backend tarafından üretilecek; frontend girmeyecek
  SubCurrAccCode?: string;
  CompanyName?: string;
  CurrencyCode?: string;
  // Yeni alanlar (ana form ile uyumlu)
  TaxOfficeCode?: string;
  TaxNumber?: string;
  IdentityNum?: string;
  IsIndividualAcc?: boolean;
  WholesalePriceGroupCode?: string;
  CustomerDiscountGrCode?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  value: SubCustomer[];
  onChange: (next: SubCustomer[]) => void;
  currencies?: { currencyCode: string; currencyDescription: string }[];
  // Lookup props (ana formdan sağlanacak)
  taxOffices?: { code: string; name: string }[];
  taxOfficesRaw?: { taxOfficeCode: string; taxOfficeDescription: string }[];
  priceGroups?: { code: string; name: string }[];
  discountGroups?: { code: string; name: string }[];
  // Ana formdaki seçili değerler (modal bu değerlere sabitlenecek)
  selectedCurrencyCode?: string;
  selectedTaxOfficeCode?: string;
  selectedPriceGroupCode?: string;
  selectedDiscountGroupCode?: string;
}

const defaultItem: SubCustomer = {
  CompanyName: '',
  CurrencyCode: 'TRY',
  IsIndividualAcc: false,
  TaxOfficeCode: '',
  TaxNumber: '',
  IdentityNum: '',
  WholesalePriceGroupCode: '',
  CustomerDiscountGrCode: '',
};

const SubCustomersModal: React.FC<Props> = ({ open, onClose, value, onChange, currencies, taxOffices, taxOfficesRaw, priceGroups, discountGroups, selectedCurrencyCode, selectedTaxOfficeCode, selectedPriceGroupCode, selectedDiscountGroupCode }) => {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<SubCustomer>({ ...defaultItem });

  const currencyOptions = useMemo(() => {
    if (currencies && currencies.length > 0) return currencies.map(c => c.currencyCode);
    return ['TRY', 'USD', 'EUR'];
  }, [currencies]);

  // Ana formdan gelen seçili değerlerle taslağı senkronize et (yeni ekleme modunda)
  React.useEffect(() => {
    if (!open) return;
    if (editIndex !== null) return; // düzenlemede kullanıcı değerini bozma
    setDraft(prev => ({
      ...prev,
      CurrencyCode: selectedCurrencyCode || prev.CurrencyCode || 'TRY',
      TaxOfficeCode: selectedTaxOfficeCode || prev.TaxOfficeCode || '',
      WholesalePriceGroupCode: selectedPriceGroupCode || prev.WholesalePriceGroupCode || '',
      CustomerDiscountGrCode: selectedDiscountGroupCode || prev.CustomerDiscountGrCode || '',
    }));
  }, [open, editIndex, selectedCurrencyCode, selectedTaxOfficeCode, selectedPriceGroupCode, selectedDiscountGroupCode]);

  const resetDraft = () => {
    setDraft({ ...defaultItem });
    setEditIndex(null);
  };

  const handleField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setDraft(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelect = (e: any) => {
    const { name, value } = e.target;
    setDraft(prev => ({ ...prev, [name]: value }));
  };

  const addOrUpdate = () => {
    // Basit zorunlu alan kontrolü: bireysel ise IdentityNum, tüzel ise TaxNumber zorunlu
    if (draft.IsIndividualAcc) {
      if (!draft.IdentityNum || draft.IdentityNum.trim() === '') {
        alert('TC Kimlik No zorunludur (Gerçek Kişi).');
        return;
      }
      if ((draft.IdentityNum || '').length !== 11) {
        alert('TC Kimlik No 11 haneli olmalıdır.');
        return;
      }
    } else {
      if (!draft.TaxNumber || draft.TaxNumber.trim() === '') {
        alert('Vergi No zorunludur (Tüzel).');
        return;
      }
      if ((draft.TaxNumber || '').length !== 10) {
        alert('Vergi No 10 haneli olmalıdır.');
        return;
      }
      if (!draft.TaxOfficeCode || draft.TaxOfficeCode.trim() === '') {
        alert('Vergi Dairesi zorunludur.');
        return;
      }
    }

    // Kayda geçerken read-only alanları ana form seçimleriyle zorla
    const payload: SubCustomer = {
      ...draft,
      // Para birimi kullanıcı seçiminden gelsin; boş ise ana form değeri ile defaultla
      CurrencyCode: draft.CurrencyCode || selectedCurrencyCode || 'TRY',
      // Vergi dairesi kullanıcı seçiminden gelsin; boş ise ana form değeri ile defaultla
      TaxOfficeCode: draft.TaxOfficeCode || selectedTaxOfficeCode || '',
      WholesalePriceGroupCode: draft.WholesalePriceGroupCode || selectedPriceGroupCode || '',
      CustomerDiscountGrCode: draft.CustomerDiscountGrCode || selectedDiscountGroupCode || '',
    };

    if (editIndex !== null) {
      const next = [...value];
      next[editIndex] = { ...payload };
      onChange(next);
    } else {
      onChange([...(value || []), { ...payload }]);
    }
    resetDraft();
  };

  const startEdit = (idx: number) => {
    setEditIndex(idx);
    setDraft({ ...(value[idx] || defaultItem) });
  };

  const removeAt = (idx: number) => {
    const next = (value || []).filter((_, i) => i !== idx);
    onChange(next);
    if (editIndex === idx) resetDraft();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alt Müşteriler</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Alt müşteri (prSubCurrAcc) kayıtlarını ekleyin/düzenleyin. Kod alanı backend tarafından otomatik üretilecektir.
        </Typography>
        {/* Gerçek kişi seçimi en üstte dursun */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="IsIndividualAcc"
                checked={!!draft.IsIndividualAcc}
                onChange={(e) => {
                  const checked = (e.target as HTMLInputElement).checked;
                  setDraft((prev) => ({
                    ...prev,
                    IsIndividualAcc: checked,
                    // Gerçek Kişi ise Vergi No'yu temizle, Tüzel ise TC'yi temizle
                    TaxNumber: checked ? '' : prev.TaxNumber,
                    IdentityNum: checked ? prev.IdentityNum : '',
                  }));
                }}
              />
            }
            label="Gerçek Kişi"
          />
        </Stack>

        {/* Müşteri/Firma Bilgisi - üstte tek satır */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <TextField
            label="Firma/İsim"
            name="CompanyName"
            value={draft.CompanyName || ''}
            onChange={handleField}
            sx={{ flex: '2 1 320px', minWidth: 240 }}
          />
          {/* Para birimi - ana formdaki gibi Select (seçilebilir) */}
          <FormControl sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <InputLabel>Para Birimi</InputLabel>
            <Select
              label="Para Birimi"
              name="CurrencyCode"
              value={draft.CurrencyCode || selectedCurrencyCode || 'TRY'}
              onChange={handleSelect}
            >
              {(currencies || []).map((c) => (
                <MenuItem key={c.currencyCode} value={c.currencyCode}>
                  {c.currencyCode} - {c.currencyDescription}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Vergi Dairesi - ana formdaki gibi Select (seçilebilir) */}
          <FormControl sx={{ flex: '2 1 320px', minWidth: 240 }}>
            <InputLabel>Vergi Dairesi</InputLabel>
            <Select
              label="Vergi Dairesi"
              name="TaxOfficeCode"
              value={draft.TaxOfficeCode || selectedTaxOfficeCode || ''}
              onChange={handleSelect}
            >
              {(taxOfficesRaw && taxOfficesRaw.length > 0
                ? taxOfficesRaw.map(o => ({ code: o.taxOfficeCode, name: o.taxOfficeDescription }))
                : (taxOffices || [])
              ).map((o) => (
                <MenuItem key={o.code} value={o.code}>
                  {o.code} - {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Kimlik/vergisel alanlar - ikinci satır */}
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1.5, mb: 1.5 }}>
          <TextField
            label="Vergi No"
            name="TaxNumber"
            value={draft.TaxNumber || ''}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, '');
              const limited = onlyDigits.slice(0, 10);
              setDraft((prev) => ({ ...prev, TaxNumber: limited }));
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
            disabled={!!draft.IsIndividualAcc}
            sx={{ flex: '1 1 200px', minWidth: 160 }}
          />
          <TextField
            label="TC Kimlik No"
            name="IdentityNum"
            value={draft.IdentityNum || ''}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, '');
              const limited = onlyDigits.slice(0, 11);
              setDraft((prev) => ({ ...prev, IdentityNum: limited }));
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 11 }}
            disabled={!draft.IsIndividualAcc}
            sx={{ flex: '1 1 200px', minWidth: 160 }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
          {/* Toptan Fiyat Grubu ana modal ile aynı ve değiştirilemez */}
          <FormControl fullWidth sx={{ flex: '1 1 0', minWidth: 220 }}>
            <InputLabel>Toptan Fiyat Grubu</InputLabel>
            <Select
              label="Toptan Fiyat Grubu"
              name="WholesalePriceGroupCode"
              value={draft.WholesalePriceGroupCode || selectedPriceGroupCode || ''}
              onChange={handleSelect}
            >
              {(priceGroups || []).map(pg => (
                <MenuItem key={pg.code} value={pg.code}>
                  {pg.code} - {pg.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ flex: '1 1 0', minWidth: 220 }}>
            <InputLabel>Hesap İskonto Grubu</InputLabel>
            <Select
              label="Hesap İskonto Grubu"
              name="CustomerDiscountGrCode"
              value={draft.CustomerDiscountGrCode || selectedDiscountGroupCode || ''}
              onChange={handleSelect}
            >
              {(discountGroups || []).map(dg => (
                <MenuItem key={dg.code} value={dg.code}>
                  {dg.code} - {dg.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Button variant="contained" color="primary" onClick={addOrUpdate}>
            {editIndex !== null ? 'Güncelle' : 'Ekle'}
          </Button>
          <Button
            variant="outlined"
            disabled={selectedIndex === null}
            onClick={() => {
              if (selectedIndex !== null) startEdit(selectedIndex);
            }}
          >
            Seçileni Düzenle
          </Button>
          {editIndex !== null && (
            <Button variant="text" onClick={resetDraft}>İptal</Button>
          )}
        </Box>

        <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Firma/İsim</TableCell>
              <TableCell>PB/Gerçek</TableCell>
              <TableCell>Vergi</TableCell>
              <TableCell>Fiyat / İskonto</TableCell>
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(value || []).map((row, idx) => (
              <TableRow
                key={idx}
                hover
                selected={selectedIndex === idx}
                onClick={() => setSelectedIndex(idx)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{row.CompanyName}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box component="span" sx={{ bgcolor: 'primary.soft', color: 'primary.main', border: theme => `1px solid ${theme.palette.primary.light}`, fontSize: 12, px: 0.75, py: 0.1, borderRadius: 1 }}>
                      {row.CurrencyCode || 'TRY'}
                    </Box>
                    <Box component="span" sx={{ bgcolor: 'grey.100', color: 'text.secondary', border: theme => `1px solid ${theme.palette.divider}`, fontSize: 12, px: 0.75, py: 0.1, borderRadius: 1 }}>
                      {row.IsIndividualAcc ? 'Gerçek' : 'Tüzel'}
                    </Box>
                  </Stack>
                  {(row.IdentityNum && row.IsIndividualAcc) ? (
                    <Box sx={{ mt: 0.25, color: 'text.secondary', fontSize: 11 }}>TC: {row.IdentityNum}</Box>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Box sx={{ lineHeight: 1.2 }}>
                    <Box sx={{ fontSize: 12, color: 'text.secondary' }}>VD: {row.TaxOfficeCode || '-'}</Box>
                    <Box sx={{ fontSize: 12 }}>VKN: {row.TaxNumber || '-'}</Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ lineHeight: 1.2 }}>
                    <Box sx={{ fontSize: 12 }}>F: {row.WholesalePriceGroupCode || '-'}</Box>
                    <Box sx={{ fontSize: 12, color: 'text.secondary' }}>İ: {row.CustomerDiscountGrCode || '-'}</Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => startEdit(idx)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => removeAt(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubCustomersModal;
